import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { FileText, Download, Loader2 } from "lucide-react";

const YEARS = Array.from({ length: 90 }, (_, i) => 2015 - i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function Reports() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [type, setType] = useState("numerology");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [birth, setBirth] = useState({
    name: "", gender: "", birth_year: "", birth_month: "", birth_day: "", birth_hour: "",
  });

  const load = () => api.get("/report-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (user) setBirth((b) => ({
      ...b,
      name: b.name || user.name || "",
      gender: b.gender || user.gender || "",
      birth_year: b.birth_year || user.birth_year || "",
      birth_month: b.birth_month || user.birth_month || "",
      birth_day: b.birth_day || user.birth_day || "",
      birth_hour: b.birth_hour === "" && user.birth_hour !== null && user.birth_hour !== undefined ? user.birth_hour : b.birth_hour,
    }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        report_type: type,
        note,
        name: birth.name || null,
        gender: birth.gender || null,
        birth_year: birth.birth_year ? Number(birth.birth_year) : null,
        birth_month: birth.birth_month ? Number(birth.birth_month) : null,
        birth_day: birth.birth_day ? Number(birth.birth_day) : null,
        birth_hour: birth.birth_hour === "" || birth.birth_hour === null || birth.birth_hour === undefined ? null : Number(birth.birth_hour),
      };
      await api.post("/report-requests", payload);
      toast.success(t("reports.applied"));
      setNote("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (!user) return null;

  const downloadReport = async (r) => {
    const token = localStorage.getItem("jl_token");
    const url = `${API}/reports/download/${r.id}`;
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `report_${r.id}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200";

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-16" data-testid="reports-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">{t("reports.subtitle")}</div>
      <h1 className="mt-3 font-serif-cn text-4xl text-[#003645] font-semibold tracking-tight">{t("reports.title")}</h1>

      <form onSubmit={submit} className="mt-8 rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-6" data-testid="reports-apply-form">
        <div className="grid sm:grid-cols-2 gap-3">
          {["numerology", "qimen"].map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setType(k)}
              className={`px-4 py-3 rounded-md text-sm font-medium border transition-colors duration-200 flex items-center gap-2 ${
                type === k ? "bg-[#005870] text-white border-[#005870]" : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e]"
              }`}
              data-testid={`report-type-${k}`}
            >
              <FileText size={14} /> {t(`reports.types.${k}`)}
            </button>
          ))}
        </div>

        {/* Applicant + birth data */}
        <div className="pt-2">
          <div className="text-xs uppercase tracking-widest text-[#00868e] font-semibold mb-3">{t("profile.birthDate")}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <input placeholder={t("auth.name")} value={birth.name} onChange={(e) => setBirth({ ...birth, name: e.target.value })} className={inputCls} data-testid="reports-name" />
            <select value={birth.gender} onChange={(e) => setBirth({ ...birth, gender: e.target.value })} className={inputCls} data-testid="reports-gender">
              <option value="">{t("profile.gender")}</option>
              <option value="male">{t("profile.male")}</option>
              <option value="female">{t("profile.female")}</option>
              <option value="other">{t("profile.other")}</option>
            </select>
            <select value={birth.birth_year} onChange={(e) => setBirth({ ...birth, birth_year: e.target.value })} className={inputCls} data-testid="reports-birth-year">
              <option value="">{t("profile.birthYear")}</option>
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={birth.birth_month} onChange={(e) => setBirth({ ...birth, birth_month: e.target.value })} className={inputCls} data-testid="reports-birth-month">
              <option value="">{t("profile.birthMonth")}</option>
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={birth.birth_day} onChange={(e) => setBirth({ ...birth, birth_day: e.target.value })} className={inputCls} data-testid="reports-birth-day">
              <option value="">{t("profile.birthDay")}</option>
              {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={birth.birth_hour} onChange={(e) => setBirth({ ...birth, birth_hour: e.target.value })} className={inputCls} data-testid="reports-birth-hour">
              <option value="">{t("profile.birthHour")}</option>
              {HOURS.map((h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00`}</option>)}
            </select>
          </div>
        </div>

        <textarea rows={3} placeholder={t("reports.noteHint")} value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} data-testid="reports-note" />
        <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] disabled:opacity-60" data-testid="reports-apply-submit">
          {submitting && <Loader2 size={14} className="animate-spin" />} {t("reports.apply")}
        </button>
      </form>

      <h2 className="mt-12 font-serif-cn text-xl text-[#003645] font-semibold">{t("reports.myRequests")}</h2>
      <div className="mt-4 rounded-lg border border-[#e5ebeb] bg-white overflow-hidden" data-testid="reports-list">
        {items.length === 0 ? (
          <div className="p-6 text-[#4a6e77]">{t("reports.empty")}</div>
        ) : (
          <ul className="divide-y divide-[#e5ebeb]">
            {items.map((r) => (
              <li key={r.id} className="p-5 flex items-center gap-4" data-testid={`report-item-${r.id}`}>
                <FileText size={18} className="text-[#005870] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[#003645] font-medium">{t(`reports.types.${r.report_type}`)}</div>
                  <div className="text-xs text-[#4a6e77]">{new Date(r.created_at).toLocaleString()}</div>
                  {r.reject_reason && <div className="text-xs text-red-700 mt-1">{r.reject_reason}</div>}
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  r.status === "completed" ? "bg-[#00868e]/10 text-[#00868e]" :
                  r.status === "rejected" ? "bg-red-50 text-red-700" :
                  "bg-amber-50 text-amber-700"
                }`}>
                  {t(`reports.statuses.${r.status}`)}
                </span>
                {r.status === "completed" && r.report_file && (
                  <button onClick={() => downloadReport(r)} className="inline-flex items-center gap-1 text-sm text-[#005870] hover:text-[#00868e] font-medium" data-testid={`download-report-${r.id}`}>
                    <Download size={14} /> {t("reports.download")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
